import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

interface Star {
  x: number;
  y: number;
  z: number;
  px: number;
  py: number;
  hue: number;
}

export function WarpSpeedTunnel() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const animationRef = useRef<number>();
  const mouseRef = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const starCount = 1200;
    const speed = 1.2;
    const maxDepth = 2000;

    const initStars = () => {
      starsRef.current = [];
      for (let i = 0; i < starCount; i++) {
        starsRef.current.push({
          x: (Math.random() - 0.5) * canvas.width * 3,
          y: (Math.random() - 0.5) * canvas.height * 3,
          z: Math.random() * maxDepth,
          px: 0,
          py: 0,
          hue: 200 + Math.random() * 160, // Blue to pink range
        });
      }
    };

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
      initStars();
    };

    const draw = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const centerX = width / 2;
      const centerY = height / 2;

      const isDark = document.documentElement.classList.contains('dark');

      // Clear with trail effect for motion blur
      ctx.fillStyle = isDark ? 'rgba(10, 10, 20, 0.15)' : 'rgba(255, 255, 255, 0.15)';
      ctx.fillRect(0, 0, width, height);

      // Mouse influence on center point (more dramatic)
      const offsetX = (mouseRef.current.x - 0.5) * 200;
      const offsetY = (mouseRef.current.y - 0.5) * 200;
      const focalX = centerX + offsetX;
      const focalY = centerY + offsetY;

      // Draw tunnel rings
      const time = Date.now() * 0.001;
      for (let i = 0; i < 5; i++) {
        const ringZ = ((time * 200 + i * 400) % maxDepth);
        const ringScale = 400 / Math.max(ringZ, 1);
        const ringRadius = Math.max(50, width * ringScale * 0.5);
        const ringOpacity = Math.max(0, 0.1 * (1 - ringZ / maxDepth));

        if (ringOpacity > 0.01) {
          ctx.beginPath();
          ctx.arc(focalX, focalY, ringRadius, 0, Math.PI * 2);
          ctx.strokeStyle = isDark
            ? `rgba(100, 150, 255, ${ringOpacity})`
            : `rgba(99, 102, 241, ${ringOpacity * 0.7})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }

      starsRef.current.forEach((star) => {
        // Move star closer (decrease z) - faster!
        star.z -= speed * 15;

        // Reset star if it's too close
        if (star.z <= 0) {
          star.x = (Math.random() - 0.5) * width * 3;
          star.y = (Math.random() - 0.5) * height * 3;
          star.z = maxDepth;
          star.px = 0;
          star.py = 0;
          star.hue = 200 + Math.random() * 160;
        }

        // Project 3D to 2D
        const scale = 400 / star.z;
        const x = star.x * scale + focalX;
        const y = star.y * scale + focalY;

        // Calculate size based on distance (bigger when closer)
        const size = Math.max(0.5, (1 - star.z / maxDepth) * 4);

        // Calculate opacity based on distance
        const opacity = Math.min(1, (1 - star.z / maxDepth) * 2);

        // Draw star trail (longer trails!)
        if (star.px !== 0 && star.py !== 0) {
          const trailLength = Math.sqrt(Math.pow(x - star.px, 2) + Math.pow(y - star.py, 2));

          if (trailLength > 1) {
            const gradient = ctx.createLinearGradient(star.px, star.py, x, y);

            if (isDark) {
              gradient.addColorStop(0, `hsla(${star.hue}, 80%, 70%, 0)`);
              gradient.addColorStop(1, `hsla(${star.hue}, 80%, 80%, ${opacity * 0.9})`);
            } else {
              gradient.addColorStop(0, `hsla(${star.hue}, 70%, 50%, 0)`);
              gradient.addColorStop(1, `hsla(${star.hue}, 70%, 50%, ${opacity * 0.7})`);
            }

            ctx.beginPath();
            ctx.moveTo(star.px, star.py);
            ctx.lineTo(x, y);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = size * 1.2;
            ctx.lineCap = 'round';
            ctx.stroke();
          }
        }

        // Draw star point with glow
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);

        if (isDark) {
          ctx.fillStyle = `hsla(${star.hue}, 70%, 90%, ${opacity})`;
        } else {
          ctx.fillStyle = `hsla(${star.hue}, 70%, 50%, ${opacity})`;
        }
        ctx.fill();

        // Add intense glow for closer stars
        if (star.z < maxDepth * 0.4) {
          const glowSize = size * 4;
          const glowGradient = ctx.createRadialGradient(x, y, 0, x, y, glowSize);

          if (isDark) {
            glowGradient.addColorStop(0, `hsla(${star.hue}, 80%, 70%, ${opacity * 0.5})`);
            glowGradient.addColorStop(0.5, `hsla(${star.hue}, 80%, 60%, ${opacity * 0.2})`);
            glowGradient.addColorStop(1, `hsla(${star.hue}, 80%, 50%, 0)`);
          } else {
            glowGradient.addColorStop(0, `hsla(${star.hue}, 70%, 60%, ${opacity * 0.4})`);
            glowGradient.addColorStop(0.5, `hsla(${star.hue}, 70%, 50%, ${opacity * 0.15})`);
            glowGradient.addColorStop(1, `hsla(${star.hue}, 70%, 40%, 0)`);
          }

          ctx.fillStyle = glowGradient;
          ctx.fillRect(x - glowSize, y - glowSize, glowSize * 2, glowSize * 2);
        }

        // Store previous position for trail
        star.px = x;
        star.py = y;
      });

      // Draw central glow
      const centralGlow = ctx.createRadialGradient(focalX, focalY, 0, focalX, focalY, 150);
      if (isDark) {
        centralGlow.addColorStop(0, 'rgba(150, 100, 255, 0.1)');
        centralGlow.addColorStop(0.5, 'rgba(100, 150, 255, 0.05)');
        centralGlow.addColorStop(1, 'rgba(100, 150, 255, 0)');
      } else {
        centralGlow.addColorStop(0, 'rgba(99, 102, 241, 0.08)');
        centralGlow.addColorStop(0.5, 'rgba(139, 92, 246, 0.03)');
        centralGlow.addColorStop(1, 'rgba(99, 102, 241, 0)');
      }
      ctx.fillStyle = centralGlow;
      ctx.fillRect(focalX - 150, focalY - 150, 300, 300);

      animationRef.current = requestAnimationFrame(draw);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      };
    };

    resize();
    animationRef.current = requestAnimationFrame(draw);

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <motion.canvas
      ref={canvasRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 -z-10 pointer-events-none"
    />
  );
}
